/**
 * Vibela landing GA4 helpers (Consent Mode v2 + funnel events).
 *
 * Key events (mark in GA4 Admin): app_modal_open, app_store_click
 * Event-scoped custom dimensions: cta_location, cta_intent, store,
 * section_name, network, link_location, link_text
 */

const GA_MEASUREMENT_ID = 'G-BRVLJQJNRK';

const APP_SMART_LINK = 'https://link.vibela.de/start';
const APP_STORE_BASE = 'https://apps.apple.com/de/app/vibela/id000000000';
const PLAY_STORE_BASE = 'https://play.google.com/store/apps/details?id=app.vibela';

/**
 * App Store Connect campaign provider token (`pt`).
 * Set MIX_APPLE_PROVIDER_TOKEN or window.__vibelaApplePt when issued.
 */
function appleProviderToken() {
  if (typeof window !== 'undefined' && window.__vibelaApplePt) {
    return String(window.__vibelaApplePt);
  }
  if (typeof process !== 'undefined' && process.env && process.env.MIX_APPLE_PROVIDER_TOKEN) {
    return String(process.env.MIX_APPLE_PROVIDER_TOKEN);
  }
  return '';
}

const QR_VIEWPORT_MIN = 768;

export function trackEvent(eventName, params = {}) {
  if (typeof window === 'undefined') {
    return;
  }
  const payload = { ...params, send_to: GA_MEASUREMENT_ID };
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, payload);
    return;
  }
  // Mirror gtag() Arguments push so queued hits work once the tag loads.
  (function () {
    window.dataLayer.push(arguments);
  })('event', eventName, payload);
}

export function isQrViewport() {
  return typeof window !== 'undefined' && window.innerWidth >= QR_VIEWPORT_MIN;
}

export function resolveCtaMeta(el, defaults = {}) {
  const fromEl = {
    cta_location:
      (el && el.getAttribute && el.getAttribute('data-cta-location')) || null,
    cta_intent:
      (el && el.getAttribute && el.getAttribute('data-cta-intent')) || null,
  };
  return {
    cta_location: fromEl.cta_location || defaults.cta_location || 'unknown',
    cta_intent: fromEl.cta_intent || defaults.cta_intent || 'app',
  };
}

function campaignSlug(ctaLocation, ctaIntent) {
  return [ctaLocation || 'unknown', ctaIntent || 'app']
    .join('_')
    .replace(/[^a-z0-9_-]/gi, '_')
    .slice(0, 100);
}

export function buildAppStoreUrl(ctaLocation, ctaIntent) {
  const url = new URL(APP_STORE_BASE);
  const pt = appleProviderToken();
  url.searchParams.set('ct', campaignSlug(ctaLocation, ctaIntent));
  if (pt) {
    url.searchParams.set('pt', pt);
  }
  url.searchParams.set('mt', '8');
  return url.toString();
}

export function buildPlayStoreUrl(ctaLocation, ctaIntent) {
  const referrer = [
    'utm_source=vibela.de',
    'utm_medium=website',
    `utm_campaign=${campaignSlug(ctaLocation, ctaIntent)}`,
  ].join('&');
  const url = new URL(PLAY_STORE_BASE);
  url.searchParams.set('referrer', referrer);
  return url.toString();
}

export function buildQrSmartLink(ctaLocation) {
  const url = new URL(APP_SMART_LINK);
  url.searchParams.set('utm_source', 'vibela.de');
  url.searchParams.set('utm_medium', 'qr');
  if (ctaLocation) {
    url.searchParams.set('utm_campaign', String(ctaLocation));
  }
  return url.toString();
}

export function buildStoreUrls(ctaLocation, ctaIntent) {
  return {
    appStoreUrl: buildAppStoreUrl(ctaLocation, ctaIntent),
    playStoreUrl: buildPlayStoreUrl(ctaLocation, ctaIntent),
    smartLink: buildQrSmartLink(ctaLocation),
  };
}

export {
  APP_SMART_LINK,
  APP_STORE_BASE,
  PLAY_STORE_BASE,
  GA_MEASUREMENT_ID,
};
